/**
 * Chat Store
 *
 * Composed store using the Zustand slice pattern. This store combines
 * all chat-related slices into a single unified store.
 *
 * Message Linking:
 * - Each AI message has a `responseTo` field linking it to the user message it's responding to
 * - This enables proper message pairing for retry functionality and UI display
 * - The link is established when creating AI messages in sendMessage()
 * - Links are preserved when loading conversation history
 *
 * Dependencies:
 * - authStore: Reads user info for sending messages (read-only)
 * - chatService: Handles API communication for chat operations
 *
 * Cleared by:
 * - useAuth hook during logout
 * - botStore when switching between different bots
 *
 * Note: clearMessages() preserves currentModel, currentBot, and currentLanguage
 * to maintain user preferences across chat sessions.
 *
 * @see https://docs.pmnd.rs/zustand/guides/slices-pattern
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ChatState, ChatStorePersistState } from './types';
import {
  createMessagesSlice,
  createConversationsSlice,
  createStreamingSlice,
  createSelectionSlice,
  createUISlice,
} from './slices';

/**
 * Creates the composed chat store by combining all slices.
 *
 * The store is wrapped with:
 * - devtools: For Redux DevTools debugging
 * - persist: For persisting user preferences (model, bot, language) to localStorage
 *
 * @example
 * // Using in a component
 * const messages = useChatStore(state => state.messages);
 * const sendMessage = useChatStore(state => state.sendMessage);
 *
 * // Using multiple selectors
 * const { messages, isLoading, sendMessage } = useChatStore(state => ({
 *   messages: state.messages,
 *   isLoading: state.isLoading,
 *   sendMessage: state.sendMessage,
 * }));
 *
 * // Accessing outside of React
 * const state = useChatStore.getState();
 * state.sendMessage('Hello!');
 */
export const useChatStore = create<ChatState>()(
  devtools(
    persist(
      (...args) => ({
        // Compose all slices together
        // Each slice provides its own state and actions
        ...createMessagesSlice(...args),
        ...createConversationsSlice(...args),
        ...createStreamingSlice(...args),
        ...createSelectionSlice(...args),
        ...createUISlice(...args),
      }),
      {
        name: 'chat-store',
        /**
         * Only persist user preferences to localStorage.
         * Conversation data is loaded from the server on demand.
         */
        partialize: (state): ChatStorePersistState => ({
          currentModel: state.currentModel,
          currentBot: state.currentBot,
          currentLanguage: state.currentLanguage,
          // Don't persist conversationId - always start fresh on refresh
        }),
      }
    ),
    {
      name: 'chat-store',
    }
  )
);

/**
 * Re-export the ChatState type for convenience
 */
export type { ChatState };
